
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Star, Heart, Gift } from 'lucide-react';

interface RidePreferencesChargeStepProps {
  preferences: string[];
  currentCharges: { [key: string]: number };
  onChargesSet: (charges: { [key: string]: number }) => void;
  onBack: () => void;
  onComplete: () => void;
}

export const RidePreferencesChargeStep: React.FC<RidePreferencesChargeStepProps> = ({
  preferences,
  currentCharges,
  onChargesSet,
  onBack,
  onComplete
}) => {
  const [charges, setCharges] = useState<{ [key: string]: number }>(currentCharges);
  const [skipCharging, setSkipCharging] = useState(false);

  const handleChargeChange = (preference: string, charge: number) => {
    setCharges(prev => ({
      ...prev,
      [preference]: charge
    }));
  };

  const handleComplete = () => {
    if (skipCharging) {
      onChargesSet({});
    } else {
      onChargesSet(charges);
    }
    onComplete();
  };

  const totalCharges = Object.values(charges).reduce((sum, charge) => sum + charge, 0);
  const goodWillPoints = preferences.length * 50; // 50 points per preference if not charged

  return (
    <div className="fixed inset-0 z-[102] bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <Button variant="ghost" onClick={onBack} size="sm" className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-lg font-semibold text-gray-800">Preference Charges</h2>
        <div className="w-10"></div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Good Will Gesture Card */}
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-green-800 mb-1">Chalo Good Will Gesture</h4>
                <p className="text-sm text-green-700 mb-2">
                  Offering preferences for free shows your generosity and builds trust with fellow travelers!
                </p>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-green-800">
                    Earn {goodWillPoints} Good Will Points
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preferences List */}
        {preferences.length > 0 ? (
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-800">Set Optional Charges</h3>
            {preferences.map(preference => (
              <Card key={preference}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{preference}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">₹</span>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={charges[preference] || 0}
                        onChange={e => handleChargeChange(preference, parseInt(e.target.value) || 0)}
                        className="w-16 px-2 py-1 border rounded text-center"
                        disabled={skipCharging}
                      />
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {preference === 'Music' && 'Let passengers enjoy their favorite tunes'}
                    {preference === 'AC' && 'Comfortable temperature for the journey'}
                    {preference === 'No Smoking' && 'Fresh, clean air throughout the ride'}
                    {preference === 'Snacks' && 'Light refreshments during travel'}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-4 text-center">
              <Gift className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No preferences selected</p>
              <p className="text-sm text-gray-400">Go back to add preferences first</p>
            </CardContent>
          </Card>
        )}

        {/* Summary */}
        {preferences.length > 0 && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <h4 className="font-semibold text-blue-800 mb-2">Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Total additional charges:</span>
                  <span className="font-medium">₹{totalCharges}</span>
                </div>
                <div className="flex justify-between">
                  <span>Good Will Points (if free):</span>
                  <span className="font-medium text-green-600">{goodWillPoints} points</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Toggle Good Will Gesture */}
        {preferences.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="goodwill"
                  checked={skipCharging}
                  onChange={e => setSkipCharging(e.target.checked)}
                  className="mt-1"
                />
                <label htmlFor="goodwill" className="flex-1 cursor-pointer">
                  <div className="font-medium text-green-700">Offer as Good Will Gesture</div>
                  <div className="text-sm text-gray-600">
                    Provide all preferences for free and earn {goodWillPoints} Good Will Points
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom Action */}
      <div className="p-4 border-t bg-white">
        <Button 
          onClick={handleComplete}
          className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white py-3 text-base font-semibold"
        >
          {skipCharging 
            ? `Continue with Good Will Gesture (+${goodWillPoints} points)` 
            : `Continue with ₹${totalCharges} charges`
          }
        </Button>
      </div>
    </div>
  );
};
